import React from "react";
import { Routes, Route, Navigate, useParams, useLocation } from "react-router-dom";
import { useAuth } from "./contexts/AuthContext";

// Components
import Layout from "./components/Layout/Layout";
import LoadingSpinner from "./components/UI/LoadingSpinner";

// Pages
import HomePage from "./pages/HomePage";
import LoginPage from "./pages/LoginPage";
import SignupPage from "./pages/SignupPage";
import RecentProjectPage from "./pages/RecentProjectPage";
import TemplatesPage from "./pages/TemplatesPage";
import ProfilePage from "./pages/ProfilePage";
import ManageTemplatesPage from "./pages/ManageTemplatesPage";
import NotFoundPage from "./pages/NotFoundPage";
import CanvasPage from "./pages/CanvasPage";
import LiteViewPage from "./pages/LiteViewPage";
import EmailVerificationPage from "./pages/EmailVerificationPage";
import VerifyEmailRequiredPage from "./pages/VerifyEmailRequiredPage";
import PrivacyPolicyPage from "./pages/PrivacyPolicyPage";
import PricingPage from "./pages/PricingPage";
import PaymentSuccessPage from "./pages/PaymentSuccessPage";
import PaymentCancelPage from "./pages/PaymentCancelPage";
import ManageSubscriptionPage from "./pages/ManageSubscriptionPage";
import BlogsPage from "./pages/BlogsPage";
import BlogPostPage from "./pages/BlogPostPage";
import ManageBlogsPage from "./pages/ManageBlogsPage";

// Protected Route Component - requires login only
const ProtectedRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return user ? children : <Navigate to="/login" replace />;
};

// Verified Route Component - requires login AND email verification
const VerifiedRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (!user.emailVerified) {
    // Pass the current location so we can redirect back after verification
    return <Navigate to="/verify-email-required" state={{ from: location }} replace />;
  }

  return children;
};

// Canvas Route - validates project ID and redirects to dashboard if missing
const CanvasRoute = ({ children }) => {
  const { projectId } = useParams();

  // Redirect to dashboard if no project ID is provided
  if (!projectId) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if authenticated)
const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  return user ? <Navigate to="/dashboard" replace /> : children;
};

function App() {
  return (
    <div className="App">
      <Routes>
        {/* Public Routes */}
        <Route path="/" element={<HomePage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/blog" element={<BlogsPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />
        <Route path="/payment/success" element={<PaymentSuccessPage />} />
        <Route path="/payment/cancel" element={<PaymentCancelPage />} />

        {/* Auth Routes */}
        <Route
          path="/login"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />
        <Route
          path="/signup"
          element={
            <PublicRoute>
              <LoginPage />
            </PublicRoute>
          }
        />

        {/* Email Verification Routes */}
        <Route
          path="/verify-email/:token"
          element={<EmailVerificationPage />}
        />
        <Route
          path="/verify-email-required"
          element={
            <ProtectedRoute>
              <VerifyEmailRequiredPage />
            </ProtectedRoute>
          }
        />

        {/* Protected Routes - Require email verification */}
        <Route
          path="/dashboard"
          element={
            <VerifiedRoute>
              <Layout>
                <RecentProjectPage />
              </Layout>
            </VerifiedRoute>
          }
        />
        <Route
          path="/profile"
          element={
            <VerifiedRoute>
              <Layout>
                <ProfilePage />
              </Layout>
            </VerifiedRoute>
          }
        />
        <Route
          path="/manage-subscription"
          element={
            <VerifiedRoute>
              <ManageSubscriptionPage />
            </VerifiedRoute>
          }
        />
        <Route
          path="/templates"
          element={
            <VerifiedRoute>
              <Layout>
                <TemplatesPage />
              </Layout>
            </VerifiedRoute>
          }
        />
        <Route
          path="/manage-templates"
          element={
            <VerifiedRoute>
              <Layout>
                <ManageTemplatesPage />
              </Layout>
            </VerifiedRoute>
          }
        />
        <Route
          path="/manage-blogs"
          element={
            <VerifiedRoute>
              <Layout>
                <ManageBlogsPage />
              </Layout>
            </VerifiedRoute>
          }
        />
        <Route
          path="/canvas/:projectId?"
          element={
            <VerifiedRoute>
              <CanvasRoute>
                <CanvasPage />
              </CanvasRoute>
            </VerifiedRoute>
          }
        />
        <Route
          path="/lite-view/:projectId?"
          element={
            <VerifiedRoute>
              <CanvasRoute>
                <LiteViewPage />
              </CanvasRoute>
            </VerifiedRoute>
          }
        />

        {/* 404 Route */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </div>
  );
}

export default App;
